/**
 * StateCard — the standard empty / error / disabled state (SPEC-09 §3.2).
 *
 * Three variants, and the third is the one that matters most here. A surface
 * behind a server flag answers 404 `feature_disabled` when the flag is off,
 * and a 404 that reaches an error boundary teaches the reader that a switch is
 * a crash. `disabled` is the honest render for that: this instance has not
 * turned the thing on, nothing is broken, and there is nothing for you to fix.
 *
 * ADR-057: chrome is monochrome. No colour is spent on these states — an empty
 * list is not a warning, and a disabled feature is not an error.
 */
import type * as React from 'react';

import { cn } from '../../lib/utils';
import { Button } from './button';

export interface StateCardProps {
  variant?: 'empty' | 'error' | 'disabled';
  /** Optional lucide (or any) icon element, rendered at 16px, muted. */
  icon?: React.ReactNode;
  title: string;
  body?: React.ReactNode;
  /** Action button. On `error` the house label is "Retry" wired to refetch. */
  actionLabel?: string;
  onAction?: () => void;
  className?: string;
  'data-testid'?: string;
}

export function StateCard({
  variant = 'empty',
  icon,
  title,
  body,
  actionLabel,
  onAction,
  className,
  'data-testid': testId,
}: StateCardProps): React.ReactElement {
  return (
    <div
      data-testid={testId}
      data-state-card={variant}
      role={variant === 'error' ? 'alert' : undefined}
      className={cn(
        'rounded-lg border border-border px-4 py-4',
        variant === 'error' && 'border-destructive/40',
        className,
      )}
    >
      <div className="flex items-start gap-3">
        {icon ? (
          <span className="mt-0.5 shrink-0 text-muted-foreground" aria-hidden>
            {icon}
          </span>
        ) : null}
        <div className="min-w-0 space-y-1">
          <p
            className={cn(
              'text-[13px] font-medium',
              variant === 'error' ? 'text-destructive' : 'text-foreground',
            )}
          >
            {title}
          </p>
          {body ? <div className="text-[13px] text-muted-foreground">{body}</div> : null}
          {actionLabel && onAction ? (
            <div className="pt-2">
              <Button size="sm" variant="outline" onClick={onAction}>
                {actionLabel}
              </Button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
